// The speed field of the current looks 01, 04, 08 and 10 (R2827;
// ui-map-interface.md §17.3.07, mockup round 117), drawn as the currents
// map of windy.com draws its wash: one texture plane under one quad.
//
// The layer owns no geometry. The engine's speed plane holds one texel a
// node of the flow grid (`chart/pieces/current-field.ts`): the speed share
// u at tide factor 1 and the wet mask. ONE full-world quad paints the wash
// (`shaders/speedfield.ts`). The chart piece `current_arrows` hands the
// grid over on a new field, a survey or a new grid, so a frame at rest
// costs one quad (§10). The scene hands the look, the reading and the tide
// over every frame; a tide step writes uniforms and no texel.
//
// **IT DRAWS WHAT THE WIRE SENDS** (v2 §14.1 rule 5). The field is
// `Hello.currents` (R2609), nil while the flag `currents` is off, and the
// layer stands invisible without it. It reads no flag.
//
// **THE GATE IS THE CHART'S, READ AT THE DRAW.** The layer draws while the
// chart shows the piece: the pack, the Chart theme, the VIEW group
// `currents` and a replay film (the piece is `film = 'hide'`) all drop it.
// A film takes the scene's frame away, so the gate is a function the layer
// asks in `visible`, after the chart's own tick, and not a value the scene
// writes.
//
// The pure halves below — the tap and the colour — are the twins of the
// shader, and the tests read them.

import type { DataTexture } from '../gfx/types';
import type { WorldEnv, WorldPass } from '../gfx/worldquad';
import { UniformValues } from '../gfx/uniforms';
import type { DrawCtx, FrameInfo, Layer } from '../layer';
import type { ChartReading } from '../chart/context';
import { CHART } from '../../gen/presentation';
import { smooth, type FlowGrid, type FlowGridPatch } from '../chart/pieces/current-field';
import { RAMP_STOPS, SPEED_UNIFORMS, rampVec4, speedProgram } from '../shaders/speedfield';

const T = CHART.pieces.current_arrows;

/** The ramp as the shader holds it: ink r, g, b and alpha a stop. */
const RAMP = rampVec4();

/** What the pass reads of one look in one reading. */
export interface SpeedLook {
  /** `field_alpha` of the look: the alpha over the ramp's, 0 for none. */
  alpha: number;
  /** `field_min` of the look: the floor of u, 0 for no floor. */
  min: number;
  /** `field_band`: the u band the floor fades in over. */
  band: number;
  /** The alpha of the reading. */
  read: number;
}

/** The figures of look `look` in `reading`. */
export function speedLookOf(look: number, reading: ChartReading): SpeedLook {
  return {
    alpha: T.arr.field_alpha[look] ?? 0,
    min: T.arr.field_min[look] ?? 0,
    band: T.fig.field_band,
    read: T[reading].alpha,
  };
}

/**
 * The colour of the wash for one tap, into `out` as premultiplied r, g, b,
 * a in 0 to 1: `main` of the shader. `share` and `wet` are the two
 * channels of the tap, `tide` the tide factor now.
 */
export function speedColour(
  share: number, wet: number, tide: number, look: SpeedLook, out: number[],
): number[] {
  const w = Math.max(0, Math.min(1, wet));
  out[0] = 0; out[1] = 0; out[2] = 0; out[3] = 0;
  if (w <= 0) return out;
  const u = Math.max(0, Math.min(1, (share / Math.max(wet, 1e-6)) * Math.abs(tide)));
  // `rampOf`: the two stops round u, mixed as `rampAt` mixes them.
  const at = T.arr.ramp_at;
  let i = 1;
  while (i < RAMP_STOPS && u > at[i]) i++;
  let r: number, g: number, b: number, ra: number;
  if (i >= RAMP_STOPS) {
    const k = 4 * (RAMP_STOPS - 1);
    r = RAMP[k]; g = RAMP[k + 1]; b = RAMP[k + 2]; ra = RAMP[k + 3];
  } else {
    const q = (u - at[i - 1]) / Math.max(1e-9, at[i] - at[i - 1]);
    const k0 = 4 * (i - 1), k1 = 4 * i;
    const mix = (j: number) => RAMP[k0 + j] + (RAMP[k1 + j] - RAMP[k0 + j]) * q;
    r = mix(0); g = mix(1); b = mix(2); ra = mix(3);
  }
  let a = Math.min(1, ra * look.alpha);
  if (look.min > 0) a *= smooth(look.min, look.min + look.band, u);
  a *= look.read * w;
  out[0] = r * a; out[1] = g * a; out[2] = b * a; out[3] = a;
  return out;
}

/** The cubic B-spline basis at the fraction f, four weights. */
function basis(f: number, w: number[]): void {
  const f2 = f * f, f3 = f2 * f;
  w[0] = (-f3 + 3 * f2 - 3 * f + 1) / 6;
  w[1] = (3 * f3 - 6 * f2 + 4) / 6;
  w[2] = (-3 * f3 + 3 * f2 + 3 * f + 1) / 6;
  w[3] = f3 / 6;
}

const WX = [0, 0, 0, 0], WY = [0, 0, 0, 0];

/**
 * The plane at the world point (x, y), into `out` as [share, wet]:
 * `speedTap` of the shader. The shader reads the 4 × 4 support as four
 * bilinear taps; this twin sums the sixteen texels with the same weights.
 * A texel past the edge is the edge texel, as clamp-to-edge reads it.
 */
export function speedTap(
  data: ArrayLike<number>, nx: number, ny: number, step: number,
  x: number, y: number, out: [number, number],
): [number, number] {
  const cx = x / step, cy = y / step;
  const bx = Math.floor(cx), by = Math.floor(cy);
  basis(cx - bx, WX);
  basis(cy - by, WY);
  let s = 0, w = 0;
  for (let j = 0; j < 4; j++) {
    const row = Math.max(0, Math.min(ny - 1, by - 1 + j));
    for (let i = 0; i < 4; i++) {
      const col = Math.max(0, Math.min(nx - 1, bx - 1 + i));
      const k = 2 * (row * nx + col), wt = WX[i] * WY[j];
      s += data[k] * wt;
      w += data[k + 1] * wt;
    }
  }
  out[0] = s;
  out[1] = w;
  return out;
}

/** The plane a speed write fills: the engine's speed texture, or a test's. */
export interface SpeedPlane {
  data: Uint8Array | Float32Array;
  width: number;
  height: number;
  markDirty(): void;
  /** Marks rows `r0` to `r1` alone for the next upload (R2897). */
  markRows?(r0: number, r1: number): void;
}

/** Writes node `k` of the grid into the plane: the speed share of a wet
 *  node against `strongest`, clamped to 1, and its wet mask; 0 on a dry
 *  node. */
function writeNode(out: Uint8Array | Float32Array, grid: FlowGrid, strongest: number, k: number): void {
  if (grid.wet[k] === 0) { out[2 * k] = 0; out[2 * k + 1] = 0; return; }
  out[2 * k] = Math.min(1, Math.hypot(grid.vx[k], grid.vy[k]) / strongest);
  out[2 * k + 1] = 1;
}

/**
 * Writes the grid into the plane: the speed share of each node at tide
 * factor 1 against `strongest`, clamped to 1, and its wet mask. A null
 * grid, a grid of another shape or a field with no current writes 0
 * everywhere. Answers whether a field stands.
 */
export function writeSpeeds(plane: SpeedPlane, grid: FlowGrid | null, strongest: number): boolean {
  const out = plane.data;
  out.fill(0);
  const ok = grid !== null && strongest > 0 && grid.nx === plane.width && grid.ny === plane.height;
  if (ok) {
    const n = Math.min(grid.nx * grid.ny, out.length >> 1);
    for (let k = 0; k < n; k++) writeNode(out, grid, strongest, k);
  }
  plane.markDirty();
  return ok;
}

/**
 * Writes the nodes a survey pass sampled alone (R2897): the first `count`
 * indices of `nodes`, which lie in rows `j0` to `j1`. The pass moved those
 * nodes and no other, and the strongest current held, so every other
 * texel already holds what `writeSpeeds` would write. The plane uploads
 * the rows alone where it can. Answers false, and writes nothing, for a
 * grid of another shape or no current: the caller writes whole.
 */
export function patchSpeeds(
  plane: SpeedPlane, grid: FlowGrid, strongest: number, nodes: Int32Array, count: number, j0: number, j1: number,
): boolean {
  if (!(strongest > 0) || grid.nx !== plane.width || grid.ny !== plane.height) return false;
  const r0 = Math.max(0, j0), r1 = Math.min(grid.ny - 1, j1);
  if (r1 < r0 || count <= 0) return true;
  const out = plane.data, n = Math.min(grid.nx * grid.ny, out.length >> 1);
  for (let i = 0; i < count; i++) {
    const k = nodes[i];
    if (k >= 0 && k < n) writeNode(out, grid, strongest, k);
  }
  if (plane.markRows) plane.markRows(r0, r1);
  else plane.markDirty();
  return true;
}

/** What the layer needs of the engine. */
export interface SpeedFieldEngine {
  readonly speed: DataTexture;
  readonly env: WorldEnv;
  newWorldPass(program: ReturnType<typeof speedProgram>): WorldPass;
}

export class SpeedFieldLayer implements Layer {
  readonly name = 'speedfield';

  private readonly pass: WorldPass;
  readonly u = new UniformValues(SPEED_UNIFORMS);
  /** The plane holds a field. */
  private hasField = false;
  private shown = true;
  /** The look and the reading the uniforms hold; -1 before the first. */
  private lookIdx = -1;
  private reading: ChartReading | null = null;
  /** `field_alpha` of the look now: 0 draws nothing. */
  private alpha = 0;
  private tide = 1;

  constructor(
    private readonly engine: SpeedFieldEngine,
    /** The chart shows the piece `current_arrows` now. */
    private readonly gate: () => boolean,
  ) {
    this.pass = engine.newWorldPass(speedProgram());
    this.pass.setExtra((p) => this.u.apply(p));
  }

  /** The layer draws while a field stands, the look lays one, nothing
   *  hides it and the chart shows the piece. */
  get visible(): boolean {
    return this.hasField && this.shown && this.alpha > 0 && this.gate();
  }
  setVisible(v: boolean): void { this.shown = v; }

  /** The look as its index in `CURRENT_LOOKS` (R2869), and the chart reading,
   *  written by the scene every frame. */
  setLook(look: number, reading: ChartReading): void {
    if (look === this.lookIdx && reading === this.reading) return;
    this.lookIdx = look;
    this.reading = reading;
    const l = speedLookOf(look, reading);
    this.alpha = l.alpha;
    this.u.set('uSfAlpha', l.alpha);
    this.u.set('uSfMin', l.min);
    this.u.set('uSfRead', l.read);
  }

  /** The tide factor now, written by the scene every frame. */
  setTide(f: number): void {
    if (f === this.tide) return;
    this.tide = f;
    this.u.set('uSfTide', f);
  }

  /**
   * The chart piece's one call (§17.3.07): the grid of a new field, a
   * survey or a new grid, and the strongest current at tide factor 1. A
   * field a survey grew in place is written again, so the call always
   * copies.
   */
  setField(grid: FlowGrid | null, strongest: number): void {
    const plane = this.engine.speed;
    this.hasField = writeSpeeds(plane, grid, strongest);
    this.u.set('uSfSize', [plane.width, plane.height]);
  }

  /**
   * The nodes of the grid the layer holds that a survey pass moved while
   * the strongest current held (R2897): the texture takes those nodes, and
   * uploads their rows alone. A plane with no field yet takes the whole
   * grid.
   */
  patchField(grid: FlowGrid, strongest: number, patch: FlowGridPatch): void {
    const plane = this.engine.speed;
    if (this.hasField && patchSpeeds(plane, grid, strongest, patch.list, patch.nodes, patch.j0, patch.j1)) return;
    this.setField(grid, strongest);
  }

  update(_frame: FrameInfo): void {
    // The scene writes the look and the tide; the piece writes the field.
  }

  draw(ctx: DrawCtx): void {
    this.pass.draw(ctx.cam, this.engine.env);
  }

  dispose(): void { this.pass.dispose(); }
}
